import { makeApi } from "@zodios/core";
import { z } from "zod";

import {
  BillingFeatureSchema,
  BillingProductSchema,
  TopUpStatusSchema,
} from "../billingConstants";
import {
  BillingAccessTokenResponseSchema,
  BillingFeaturesResponseSchema,
  BillingSettingsResponseSchema,
  FeatureLimitsResponseSchema,
  MigrationStatusResponseSchema,
  NotifyEmailsResponseSchema,
  TopUpsResponseSchema,
  UpdateFeatureLimitsResponseSchema,
  UpdateNotifyEmailsResponseSchema,
} from "../models";

const api = makeApi([
  {
    alias: "getMigrationStatus",
    method: "get",
    path: "/api/v1/billing/migration-status",
    response: MigrationStatusResponseSchema,
  },
  {
    alias: "getFeatureLimits",
    method: "get",
    path: "/api/v1/billing/feature-limits",
    parameters: [
      {
        type: "Query",
        name: "product",
        schema: BillingProductSchema.optional(),
      },
      {
        type: "Query",
        name: "feature",
        schema: BillingFeatureSchema.optional(),
      },
    ],
    response: FeatureLimitsResponseSchema,
  },
  {
    alias: "getFeatures",
    method: "get",
    path: "/api/v1/billing/features",
    parameters: [
      {
        type: "Query",
        name: "product",
        schema: BillingProductSchema.optional(),
      },
    ],
    response: BillingFeaturesResponseSchema,
  },
  {
    alias: "getTopups",
    method: "get",
    path: "/api/v1/billing/topups",
    parameters: [
      {
        type: "Query",
        name: "dateFrom",
        schema: z.string().date(),
      },
      {
        type: "Query",
        name: "dateTo",
        schema: z.string().date(),
      },
      {
        type: "Query",
        name: "status",
        schema: TopUpStatusSchema.optional(),
      },
    ],
    response: TopUpsResponseSchema,
  },
  {
    alias: "getNotifyEmails",
    method: "get",
    path: "/api/v1/billing/notify-emails",
    response: NotifyEmailsResponseSchema,
  },
  {
    alias: "updateNotifyEmails",
    method: "put",
    path: "/api/v1/billing/notify-emails",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          notifyEmails: z.array(z.string().email()),
        }),
      },
    ],
    response: UpdateNotifyEmailsResponseSchema,
  },
  {
    alias: "getBillingSettings",
    method: "get",
    path: "/api/v1/billing/settings",
    response: BillingSettingsResponseSchema,
  },
  {
    alias: "updateBillingSettings",
    method: "patch",
    path: "/api/v1/billing/settings",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          monthlyReportEnabled: z.boolean().optional(),
        }),
      },
    ],
    response: BillingSettingsResponseSchema,
  },
  {
    alias: "updateFeatureLimits",
    method: "put",
    path: "/api/v1/billing/feature-limits",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          featureLimits: z.array(
            z.object({
              product: BillingProductSchema,
              feature: BillingFeatureSchema,
              limit: z.number(),
              isActive: z.boolean().optional(),
            }),
          ),
        }),
      },
    ],
    response: UpdateFeatureLimitsResponseSchema,
  },
  {
    alias: "createAccessToken",
    method: "post",
    path: "/api/v1/billing/access-token",
    response: BillingAccessTokenResponseSchema,
  },
  {
    alias: "exportBillingUsage",
    method: "post",
    path: "/api/v1/billing/usage-export",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: z.object({
          month: z.string(),
        }),
      },
    ],
    response: z.instanceof(Blob),
  },
]);

export { api };
